import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import epaperService, { isRequestCanceled } from "../../../apiService/epaper.service";
import {
  DEFAULT_DATE,
  PAGE_SIZE,
  SEARCH_DEBOUNCE_MS,
  STALE_PENDING_MINUTES,
  UPDATE_POLL_INTERVAL_MS,
  UPDATE_POLL_TIMEOUT_MS,
} from "../constants";
import {
  formatDateForApi,
  formatDateLabel,
  getEditionApiValue,
  isEditionUpdateComplete,
  metricCards,
  parseDashboardPayload,
  sleep,
  statusToApiValue,
} from "../utils";

const EMPTY_SUMMARY = {
  editions: [],
  totalEditions: 0,
  successCount: 0,
  failedCount: 0,
  pendingCount: 0,
  notPublishedCount: 0,
  totalPages: 1,
  totalExecutionTime: "0s",
};

const buildDashboardParams = ({
  fromDateApi,
  toDateApi,
  isMultiDayRange,
  page,
  limit,
  status,
  search,
  overrides = {},
}) => {
  const params = {
    date: fromDateApi,
    page: overrides.page ?? page,
    limit: overrides.limit ?? limit,
  };
  if (isMultiDayRange) {
    params.fromDate = fromDateApi;
    params.toDate = toDateApi;
  }
  const statusFilter = overrides.status !== undefined ? overrides.status : status;
  if (statusFilter && statusFilter !== "All Status") {
    params.status = statusToApiValue(statusFilter) || statusFilter;
  }
  const searchFilter = overrides.search !== undefined ? overrides.search : search.trim();
  if (searchFilter) {
    params.search = searchFilter;
  }
  return params;
};

export function useEpaperDashboard() {
  const [editions, setEditions] = useState([]);
  const [totalEditions, setTotalEditions] = useState(0);
  const [status, setStatus] = useState("All Status");
  const [selectedRange, setSelectedRange] = useState({ from: DEFAULT_DATE, to: DEFAULT_DATE });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(DEFAULT_DATE);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [emptyMessage, setEmptyMessage] = useState("");
  const [lastSyncAt, setLastSyncAt] = useState(() => new Date());
  const [totalExecutionTime, setTotalExecutionTime] = useState("0s");
  const [summarySuccessCount, setSummarySuccessCount] = useState(0);
  const [summaryFailedCount, setSummaryFailedCount] = useState(0);
  const [summaryPendingCount, setSummaryPendingCount] = useState(0);
  const [summaryNotPublishedCount, setSummaryNotPublishedCount] = useState(0);

  const lastSyncIsoRef = useRef("");
  const mainAbortRef = useRef(null);
  const mainSeqRef = useRef(0);
  const pollGenerationRef = useRef(0);
  const mountedRef = useRef(true);
  const searchQueryRef = useRef("");

  const rangeFrom = selectedRange?.from || null;
  const rangeTo = selectedRange?.to || selectedRange?.from || null;
  const fromDateApi = formatDateForApi(rangeFrom);
  const toDateApi = formatDateForApi(rangeTo);
  const isMultiDayRange =
    Boolean(selectedRange?.from && selectedRange?.to) && fromDateApi !== toDateApi;
  const updateDateApi = fromDateApi;
  const rangeLabel = !rangeFrom
    ? "Select Date"
    : isMultiDayRange
    ? `${formatDateLabel(rangeFrom)} - ${formatDateLabel(rangeTo)}`
    : formatDateLabel(rangeFrom);

  const cards = useMemo(
    () =>
      metricCards(
        totalEditions || editions.length,
        summarySuccessCount,
        summaryFailedCount + summaryNotPublishedCount,
        totalExecutionTime,
        lastSyncAt,
        summaryPendingCount,
        summaryNotPublishedCount
      ),
    [
      totalEditions,
      editions.length,
      summarySuccessCount,
      summaryFailedCount,
      summaryNotPublishedCount,
      totalExecutionTime,
      lastSyncAt,
      summaryPendingCount,
    ]
  );
  const stalePendingIds = useMemo(() => {
    const now = Date.now();
    return editions
      .filter((item) => {
        if (item.status !== "Pending" || item.isNotPublished) return false;
        const updatedAtMs = new Date(item.updatedAt).getTime();
        return (now - updatedAtMs) / (1000 * 60) >= STALE_PENDING_MINUTES;
      })
      .map((item) => item.id);
  }, [editions]);
  const visiblePageNumbers = useMemo(() => {
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    return Array.from({ length: end - adjustedStart + 1 }, (_, index) => adjustedStart + index);
  }, [page, totalPages]);

  const applyEmptyDashboard = useCallback(() => {
    setEditions(EMPTY_SUMMARY.editions);
    setTotalEditions(EMPTY_SUMMARY.totalEditions);
    setSummarySuccessCount(EMPTY_SUMMARY.successCount);
    setSummaryFailedCount(EMPTY_SUMMARY.failedCount);
    setSummaryPendingCount(EMPTY_SUMMARY.pendingCount);
    setSummaryNotPublishedCount(EMPTY_SUMMARY.notPublishedCount);
    setTotalPages(EMPTY_SUMMARY.totalPages);
    setTotalExecutionTime(EMPTY_SUMMARY.totalExecutionTime);
  }, []);

  const fetchDashboard = useCallback(
    async ({ silent = false, apply = true, overrides = {} } = {}) => {
      if (!fromDateApi) return null;

      let seq = null;
      let signal;
      if (apply) {
        mainAbortRef.current?.abort();
        const controller = new AbortController();
        mainAbortRef.current = controller;
        signal = controller.signal;
        seq = ++mainSeqRef.current;
      }

      if (!silent && apply) {
        setIsLoading(true);
        setFetchError("");
        setEmptyMessage("");
      }

      try {
        const params = buildDashboardParams({
          fromDateApi,
          toDateApi,
          isMultiDayRange,
          page,
          limit: PAGE_SIZE,
          status,
          search: debouncedSearch,
          overrides,
        });
        const response = await epaperService.fetchDashboard(params, { signal });
        if (apply && seq !== mainSeqRef.current) return null;

        const parsed = parseDashboardPayload(response.data || {});
        if (apply) {
          setEditions(parsed.normalized);
          setTotalEditions(parsed.totalEditions);
          setSummarySuccessCount(parsed.successCount);
          setSummaryFailedCount(parsed.failedCount);
          setSummaryPendingCount(parsed.pendingCount);
          setSummaryNotPublishedCount(parsed.notPublishedCount);
          const pagesFromTotal = Math.max(1, Math.ceil(Number(parsed.totalEditions || 0) / PAGE_SIZE) || 1);
          setTotalPages(parsed.pagination?.totalPages || pagesFromTotal);
          setTotalExecutionTime(parsed.totalExecutionTime);
          if (parsed.lastSyncIso) {
            lastSyncIsoRef.current = parsed.lastSyncIso;
            setLastSyncAt(new Date(parsed.lastSyncIso));
          }
          setEmptyMessage(
            parsed.normalized.length === 0 ? "No editions available for the selected filters." : ""
          );
        }

        return {
          editions: parsed.normalized,
          lastSyncIso: parsed.lastSyncIso || lastSyncIsoRef.current,
          successCount: parsed.successCount,
          notPublishedCount: parsed.notPublishedCount,
        };
      } catch (error) {
        if (isRequestCanceled(error) || (apply && seq !== mainSeqRef.current)) {
          return null;
        }
        if (silent) return null;
        applyEmptyDashboard();
        if (error?.response?.status === 404) {
          setEmptyMessage("No editions found for this date.");
        } else {
          const serverError = error?.response?.data?.message || error?.message;
          setFetchError(`Failed to load dashboard data. ${serverError || ""}`.trim());
        }
        return null;
      } finally {
        if (!silent && apply && seq === mainSeqRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      applyEmptyDashboard,
      debouncedSearch,
      fromDateApi,
      isMultiDayRange,
      page,
      status,
      toDateApi,
    ]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pollGenerationRef.current += 1;
      mainAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = search.trim();
      if (nextSearch === searchQueryRef.current) return;
      searchQueryRef.current = nextSearch;
      setDebouncedSearch(nextSearch);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const waitForDashboardRefresh = useCallback(
    async ({
      previousEditions = [],
      waitForEditions = false,
      previousLastSyncIso = "",
      previousSuccessCount,
      previousNotPublishedCount,
      intervalMs = UPDATE_POLL_INTERVAL_MS,
      timeoutMs = UPDATE_POLL_TIMEOUT_MS,
    } = {}) => {
      const generation = pollGenerationRef.current;
      const isStale = () => !mountedRef.current || generation !== pollGenerationRef.current;
      const startedAt = Date.now();

      try {
        while (Date.now() - startedAt < timeoutMs) {
          await sleep(intervalMs);
          if (isStale()) return null;

          if (waitForEditions && previousEditions.length) {
            const stillProcessing = [];
            for (const edition of previousEditions) {
              if (isStale()) return null;
              const slug = getEditionApiValue(edition);
              const queries = Array.from(new Set([slug, edition.name].filter(Boolean)));
              let row = null;
              for (const query of queries) {
                const snapshot = await fetchDashboard({
                  silent: true,
                  apply: false,
                  overrides: { search: query, page: 1, status: "", limit: PAGE_SIZE },
                });
                row =
                  snapshot?.editions?.find(
                    (item) =>
                      item.id === edition.id ||
                      item.slug === slug ||
                      getEditionApiValue(item) === slug
                  ) || null;
                if (row) break;
              }
              if (!isEditionUpdateComplete(row)) {
                stillProcessing.push(edition);
              }
            }
            if (stillProcessing.length === 0) {
              await fetchDashboard({ silent: true });
              return true;
            }
            continue;
          }

          const snapshot = await fetchDashboard({ silent: true });
          if (!snapshot || isStale()) continue;
          const syncChanged =
            Boolean(previousLastSyncIso) && snapshot.lastSyncIso !== previousLastSyncIso;
          const summaryChanged =
            (previousSuccessCount != null && snapshot.successCount !== previousSuccessCount) ||
            (previousNotPublishedCount != null &&
              snapshot.notPublishedCount !== previousNotPublishedCount);
          if (syncChanged || summaryChanged) {
            return snapshot;
          }
        }
        if (!isStale()) {
          await fetchDashboard({ silent: true });
        }
        return null;
      } catch (error) {
        if (isRequestCanceled(error) || isStale()) return null;
        throw error;
      }
    },
    [fetchDashboard]
  );

  const flushSearch = useCallback((nextSearch) => {
    searchQueryRef.current = nextSearch;
    setSearch(nextSearch);
    setDebouncedSearch(nextSearch);
  }, []);

  const clearFilters = useCallback(() => {
    setStatus("All Status");
    setSelectedRange({ from: DEFAULT_DATE, to: DEFAULT_DATE });
    flushSearch("");
    setPage(1);
    setVisibleMonth(DEFAULT_DATE);
    setIsCalendarOpen(false);
  }, [flushSearch]);

  const handleSelectRange = useCallback((range) => {
    setSelectedRange(range || { from: undefined, to: undefined });
    if (range?.from) {
      setVisibleMonth(range.from);
    }
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((nextSearch) => {
    setSearch(nextSearch);
  }, []);

  const toggleCalendar = useCallback((month) => {
    setVisibleMonth(month || DEFAULT_DATE);
    setIsCalendarOpen((prev) => !prev);
  }, []);

  const closeCalendar = useCallback(() => {
    setIsCalendarOpen(false);
  }, []);

  const goToPrevPage = useCallback(() => {
    setPage((prev) => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  return {
    editions,
    totalEditions,
    status,
    selectedRange,
    search,
    page,
    totalPages,
    isCalendarOpen,
    visibleMonth,
    setVisibleMonth,
    isLoading,
    fetchError,
    emptyMessage,
    lastSyncAt,
    summaryFailedCount,
    summaryNotPublishedCount,
    isMultiDayRange,
    updateDateApi,
    rangeLabel,
    rangeFrom,
    cards,
    stalePendingIds,
    visiblePageNumbers,
    fetchDashboard,
    waitForDashboardRefresh,
    clearFilters,
    handleSelectRange,
    handleStatusChange,
    handleSearchChange,
    toggleCalendar,
    closeCalendar,
    goToPrevPage,
    goToNextPage,
    setPage,
  };
}
